import { useQuery } from "@tanstack/react-query";
import request from "graphql-request";
import { useRef } from "react";
import { LocomotiveScrollProvider } from "react-locomotive-scroll";
import { useParams } from "react-router-dom";
import { projectBySlugQuery } from "../../../infrastructure/graphql/singleProject";
import { Picture } from "../../../infrastructure/types/images";
import { Project as ProjectType } from "../../../infrastructure/types/project";
import Footer from "../../components/footer";
import ImageWrapper from "../../components/imageWrapper";
import Flex from "../../components/ui/Flex/Flex";
import "./style.scss";

const Project = (): JSX.Element => {
  const { slug: slugParam } = useParams();

  const containerRef = useRef(null);

  const endpoint = process.env.REACT_APP_SANITY_URL;

  if (endpoint === undefined)
    throw new Error("undefined env variable -> sanity url");

  const { data: project, isLoading } = useQuery<ProjectType>(
    ["single-project", slugParam],
    async () => {
      const data = await request(endpoint, projectBySlugQuery, {
        slug: slugParam,
      });

      return data.allSingleProject[0];
    }
  );

  const urlFor = (image: Picture) => {
    return image.image.asset.url;
  };

  const altTextFor = (image: Picture) => {
    return image.image.alt_text;
  };

  return (
    <>
      {isLoading || project === undefined ? (
        <div>Loading...</div>
      ) : (
        <LocomotiveScrollProvider
          options={{
            smooth: true,
            multiplier: 1.5,
            class: "is-reveal",
            offset: [0, 0],
            repeat: true,
            lerp: 0.2,
          }}
          containerRef={containerRef}
        >
          <main data-scroll-container ref={containerRef}>
            <Flex justifyBetween>
              <div className="section-wrapper project__wrapper">
                <h1 className="project__title font-weight-7 flex self-start">
                  {project.project_title}
                </h1>
                <p>{project.project_summary}</p>
                <p>
                  {" "}
                  {project.project_date
                    ? new Date(project?.project_date).toLocaleDateString()
                    : null}
                </p>
              </div>
            </Flex>
            <div className="hero__wrapper">
              <ImageWrapper
                src={urlFor(project.project_hero_image)}
                alt={altTextFor(project.project_hero_image)}
                data-scroll-speed="1.5"
                data-scroll
                data-scroll-repeat
              />
            </div>
            <div
              className="main__image__wrapper  py-10"
              style={{ backgroundColor: project.project_image_header.color }}
            >
              <div className="main__image__container section-wrapper">
                <ImageWrapper
                  alt={altTextFor(project.project_image_header.image)}
                  src={urlFor(project.project_image_header.image)}
                />
              </div>
            </div>
            <div
              className="content__wrapper"
              style={{ backgroundColor: project.project_content_images.color }}
            >
              <div className="section-wrapper">
                <div className="content__wrapper-content py-10">
                  <div id="img-1" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_1.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_1
                      )}
                    />
                  </div>
                  <div id="img-2" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_2.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_2
                      )}
                    />
                  </div>
                  <div id="img-3" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_3.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_3
                      )}
                    />
                  </div>
                  <div id="img-4" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_4.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_4
                      )}
                    />
                  </div>
                  <div id="img-5" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_5.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_5
                      )}
                    />
                  </div>
                  <div id="img-6" className="content__wrapper-img">
                    <ImageWrapper
                      alt={
                        project.project_content_images.project_image_6.image
                          .alt_text
                      }
                      src={urlFor(
                        project.project_content_images.project_image_6
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              className="footer__image__wrapper py-10"
              style={{ backgroundColor: project.project_image_footer.color }}
            >
              <div className="footer__image__container section-wrapper">
                <ImageWrapper
                  alt={altTextFor(project.project_image_footer.image)}
                  src={urlFor(project.project_image_footer.image)}
                />
              </div>
            </div>
            <Footer />
          </main>
        </LocomotiveScrollProvider>
      )}
    </>
  );
};

export default Project;
